'use client';

// Shared pieces for chart images attached to comments — used by the feed card,
// the trade detail modal and the idea page so all three look identical.
import { useState, useRef } from 'react';
import { ImagePlus, X, Maximize2 } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';

/**
 * onPaste handler for comment inputs: lifts a pasted image (e.g. a TradingView
 * screenshot copied to the clipboard) straight into the attachment slot.
 * Any image/* type is accepted — compression re-encodes to JPEG before upload.
 */
export function makePasteHandler(onPick: (file: File) => void) {
  return (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          onPick(file);
          return;
        }
      }
    }
  };
}

/** Paper-clip style attach button that opens the file picker. */
export function CommentImageButton({
  disabled,
  onPick,
}: {
  disabled?: boolean;
  onPick: (file: File) => void;
}) {
  const { t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.currentTarget.value = ''; // same file can be re-picked after removing it
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="px-2.5 rounded-lg transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        style={{ border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'var(--bg-sunken)' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        title={t('Attach a chart screenshot')}
        aria-label={t('Attach a chart screenshot')}
      >
        <ImagePlus size={15} />
      </button>
    </>
  );
}

/** Thumbnail of the picked image shown above the input, with a remove button. */
export function CommentImageDraft({
  preview,
  uploading,
  onRemove,
}: {
  preview: string;
  uploading?: boolean;
  onRemove: () => void;
}) {
  const { t } = useLang();
  return (
    <div className="relative inline-block mb-2 align-top">
      <img
        src={preview}
        alt={t('Attached chart')}
        className="h-16 rounded-lg border object-cover"
        style={{ borderColor: 'var(--border)', maxWidth: 180, background: 'var(--bg-sunken)' }}
      />
      {uploading && (
        <div
          className="absolute inset-0 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(28,43,38,0.55)' }}
        >
          <span className="text-[10px] font-semibold animate-pulse" style={{ color: '#ffffff' }}>
            {t('Uploading…')}
          </span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={uploading}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center disabled:opacity-40"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        aria-label={t('Remove image')}
      >
        <X size={11} />
      </button>
    </div>
  );
}

/** The image inside a posted comment — compact, rounded, click to view full size. */
export function CommentImageView({ url }: { url: string }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="relative block mt-2 w-full max-w-[460px] text-left group cursor-zoom-in"
        aria-label={t('Open chart image')}
      >
        {/* Server-side validation guarantees landscape (aspect 1.3–5.0), so the
            natural height at ≤460px width stays ≤ ~355px — never crop a chart,
            the time axis and SL/TP labels are the point of attaching it. */}
        <img
          src={url}
          alt={t('Chart attachment')}
          loading="lazy"
          className="rounded-lg w-full h-auto border"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-sunken)' }}
        />
        <span
          className="absolute bottom-1.5 right-1.5 w-6 h-6 rounded-md items-center justify-center hidden group-hover:flex"
          style={{ background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.2)', color: '#ffffff' }}
        >
          <Maximize2 size={12} />
        </span>
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center p-4 animate-fade-in"
          style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
        >
          <img
            src={url}
            alt={t('Chart attachment')}
            className="max-w-full max-h-full rounded-xl"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.8)' }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            aria-label={t('Close')}
          >
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
}
